import { instancePath } from "@/lib/instance";
import { useEffect, useState } from "react";
import { getRouteApi, Navigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { AuthCard } from "@/components/AuthCard";
import { errorMessage, meQuery, requestMagicLink } from "@/lib/api";

const route = getRouteApi("/login");

export function LoginPage() {
  const { t } = useTranslation();
  const { redirect, error, email: invitedEmail } = route.useSearch();
  const me = useQuery(meQuery);
  const [email, setEmail] = useState(invitedEmail ?? "");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => requestMagicLink(email, redirect),
    onSuccess: () => setSentTo(email),
  });

  useEffect(() => {
    if (instancePath) {
      const path = redirect ?? "/";
      const slug = path.split("/").filter(Boolean)[0];
      const target = slug ? `/t/${slug}?next=${encodeURIComponent(instancePath + path)}` : "/";
      location.replace(`/login?redirect=${encodeURIComponent(target)}`);
    }
  }, [redirect]);
  if (instancePath) return null;
  if (me.data && redirect?.startsWith("/t/")) { location.replace(redirect); return null; }
  if (me.data) return <Navigate to={redirect && redirect.startsWith("/") ? redirect : "/"} />;

  if (sentTo) {
    return (
      <AuthCard title={t("auth.checkInbox")}>
        <p className="text-[13px] text-muted">
          <Trans i18nKey="auth.sentTo" values={{ email: sentTo }} components={{ 1: <span className="font-medium text-fg" /> }} />
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <button className="btn" onClick={() => setSentTo(null)}>
            {t("auth.useDifferent")}
          </button>
          {import.meta.env.DEV && !instancePath && (
            <a className="text-[12px] text-accent hover:underline" href={`${instancePath}/dev/outbox`}>
              {t("auth.devOutbox")}
            </a>
          )}
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("auth.title")}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          send.mutate();
        }}
      >
        <label className="flex flex-col gap-1.5 text-[12px] font-medium text-muted">
          {t("auth.email")}
          <input
            className="input h-9"
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            placeholder={t("auth.emailPlaceholder")}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        {error === "link_invalid" && <p className="text-[12px] text-danger">{t("auth.errors.link_invalid")}</p>}
        {send.isError && <p className="text-[12px] text-danger">{errorMessage(send.error)}</p>}
        <button className="btn btn-primary h-9 justify-center" type="submit" disabled={send.isPending || !email}>
          {send.isPending ? t("auth.sending") : t("auth.sendLink")}
        </button>
      </form>
    </AuthCard>
  );
}
